import {
  useEffect,
  useMemo,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import type { SerializedStyles } from "@emotion/react";
import Search from "@mui/icons-material/Search";
import { Autocomplete, Box, createFilterOptions } from "@mui/material";
import type {
  AutocompleteProps,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { Element } from "react-scroll";

import { normaliseForSearch } from "@thalamos/common";

import type { TextFieldProps } from "../TextField/TextField.js";
import { TextField } from "../TextField/TextField.js";
import { createFuse } from "~/lib/compat/fuse.js";

const getOptionFilterText = (
  option: Record<string, unknown>,
  optionsKey: string,
): string => {
  const value = option[optionsKey];

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return "";
};

// A JSX element is an immutable description, not a component instance: React
// mounts a separate icon wherever this is rendered. One shared element keeps
// `startAdornment`'s identity stable; MUI's InputBase compares it in an effect
// that sets FormControl state, so a fresh element per render fires on every
// keystroke.
const SEARCH_ADORNMENT = <Search fontSize="medium" color="primary" />;

export interface TypeaheadProps<T> extends TextFieldProps {
  resetKey?: string;
  label?: string;
  options: T[];
  getOptionLabel?: (option: string | T) => string;
  onInputChange?: (value: string) => void;
  onChange?: (value: T | string | null) => void;
  onFocus?: () => void;
  disabled?: boolean;
  loading?: boolean;
  inputValue?: string;
  testId?: string;
  maxLength?: number;
  allowFreeText?: boolean;
  name: string;
  optionsKey?: string;
  enableFuse?: boolean;
  /**
   * Opt in to non-fuzzy, deterministic filtering: normalised
   * (accent/punctuation/case-insensitive) token matching that keeps results in
   * the caller's `options` order (e.g. alphabetical) rather than re-ranking
   * them. Takes precedence over `enableFuse`. Use when you want good matching
   * without fuzzy scoring or the per-keystroke reordering it causes.
   */
  deterministicSearch?: boolean;
  groupBy?: (option: T) => string;
  css?: SerializedStyles;
  getOptionSubLabel?: (option: T) => string;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  disablePortal?: boolean;
  slotProps?: AutocompleteProps<T, false, false, boolean>["slotProps"];
}

const defaultProps = {
  loading: false,
  onInputChange: () => {},
  onFocus: undefined,
  label: undefined,
  placeholder: undefined,
  subtext: undefined,
  subtextIcon: undefined,
  errorMessage: undefined,
  inputValue: "",
  onBlur: undefined,
  disabled: false,
  InputProps: undefined,
  maxLength: undefined,
  allowFreeText: true,
  optionsKey: "label",
  enableFuse: true,
  deterministicSearch: false,
  groupBy: undefined,
  renderOption: undefined,
} satisfies Partial<TypeaheadProps<unknown>>;

export const Typeahead = <T,>(props: TypeaheadProps<T>) => {
  const {
    resetKey,
    label,
    options,
    getOptionLabel,
    onInputChange,
    onChange,
    disabled,
    loading,
    inputValue,
    maxLength,
    inputProps,
    allowFreeText,
    name,
    optionsKey,
    enableFuse,
    deterministicSearch,
    errorMessage,
    onBlur,
    onFocus,
    groupBy,
    getOptionSubLabel,
    renderOption,
    getOptionDisabled,
    disablePortal,
    slotProps: autocompleteSlotProps,
    testId: _testId,
    ...rest
  } = { ...defaultProps, ...props };

  const theme = useTheme();

  const testId = props.testId ? `field:${props.testId}` : undefined;
  const resolvedInputValue = inputValue;

  // A new resetKey asks the parent to clear the text. Skipped on mount so a
  // field that opens with a committed value keeps it.
  const previousResetKey = useRef(resetKey);
  useEffect(() => {
    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      onInputChange("");
    }
  }, [resetKey, onInputChange]);

  const resolvedOptionsKey = optionsKey;
  const inputValueLowerCase = resolvedInputValue.toLowerCase();

  // Build the normalised index and Fuse instance once per options list rather
  // than on every keystroke. Normalising both query and option text makes the
  // match punctuation/accent/case-insensitive (e.g. "Queens" finds "Queen's");
  // `ignoreLocation` lets the match land anywhere in the "name, address"
  // combined labels. Memoising avoids re-indexing large lists (hospitals /
  // trusts) per keystroke, which would lag on lower-powered devices. Only built
  // when fuzzy search will actually be used, so consumers that opt out (via
  // `deterministicSearch` or `enableFuse={false}`) don't pay to index the list.
  const fuzzySearch = useMemo(() => {
    if (!enableFuse || deterministicSearch) return null;
    const indexed = options.map((option) => ({
      option,
      searchText: normaliseForSearch(
        getOptionFilterText(option as Record<string, unknown>, optionsKey),
      ),
    }));
    const fuse = createFuse(indexed, {
      keys: ["searchText"],
      ignoreLocation: true,
    });
    return (query: string): T[] => {
      const normalisedQuery = normaliseForSearch(query);
      if (!normalisedQuery) return options;
      return fuse.search(normalisedQuery).map(({ item }) => item.option);
    };
  }, [options, optionsKey, enableFuse, deterministicSearch]);

  // Non-fuzzy alternative to `fuzzySearch`. An option matches when every
  // whitespace-separated query token is a substring of its normalised text, so
  // word order and mid-name words work and "queens" finds "Queen's" (see
  // `normaliseForSearch`). Matches are returned in the caller's own `options`
  // order (e.g. alphabetical) — no relevance re-ranking, no per-keystroke
  // jitter. The normalised text is indexed once here (not per keystroke), and
  // the whole thing is only built when opted in.
  const deterministicFilter = useMemo(() => {
    if (!deterministicSearch) return null;
    const indexed = options.map((option) => ({
      option,
      text: normaliseForSearch(
        getOptionFilterText(option as Record<string, unknown>, optionsKey),
      ),
    }));
    return (query: string): T[] => {
      const normalisedQuery = normaliseForSearch(query);
      if (!normalisedQuery) return options;
      const tokens = normalisedQuery.split(" ");
      return indexed
        .filter((entry) => tokens.every((token) => entry.text.includes(token)))
        .map((entry) => entry.option);
    };
  }, [options, optionsKey, deterministicSearch]);

  // MUI's default filter matches surrounding spaces literally, so " Mary"
  // would miss "Mary AC".
  const trimmedFilterOptions = useMemo(
    () => createFilterOptions<T>({ trim: true }),
    [],
  );

  // A stable identity per option, used for option keys (so MUI does not reuse
  // or duplicate rows as the filtered list reorders) and value equality (so the
  // selected/highlighted option is tracked correctly across renders).
  const keyOf = (option: string | T): string => {
    if (typeof option === "string") {
      return option;
    }
    const text = getOptionFilterText(
      option as Record<string, unknown>,
      optionsKey,
    );
    if (text) {
      return text;
    }
    return getOptionLabel ? getOptionLabel(option) : "";
  };

  return (
    <Element name={name} data-testid={testId} style={{ position: "relative" }}>
      <Autocomplete<T, false, false, boolean>
        disablePortal={disablePortal}
        slotProps={autocompleteSlotProps}
        filterOptions={
          deterministicFilter
            ? // Non-fuzzy, order-preserving normalised search. Filter on the
              // input MUI reports in `state` (see the fuse branch below for why).
              (_options, state) => deterministicFilter(state.inputValue)
            : enableFuse
              ? // Filter on the input MUI reports in `state`, not the controlled
                // `resolvedInputValue`: on a pristine re-open MUI passes "" here,
                // so the full list shows and a committed value can be changed.
                // Using resolvedInputValue filtered down to the committed value.
                // (`fuzzySearch` is always built when this branch is reachable;
                // the `?? options` is just a null-safe fallback for the types.)
                (_options, state) => fuzzySearch?.(state.inputValue) ?? options
              : options.some(
                    (option) =>
                      getOptionFilterText(
                        option as Record<string, unknown>,
                        resolvedOptionsKey,
                      ).toLowerCase() === inputValueLowerCase,
                  )
                ? (options) => options
                : trimmedFilterOptions
        }
        fullWidth
        onBlur={onBlur}
        onFocus={onFocus}
        freeSolo={allowFreeText}
        // The selection is the text itself, so `value` is derived from the
        // controlled `inputValue`. Never mirror it into state with an effect:
        // an update scheduled from an effect on every keystroke leaves work
        // pending after each commit, and once keystrokes outrun React's
        // scheduler that counts towards the nested-update limit ("Maximum
        // update depth exceeded").
        value={resolvedInputValue}
        inputValue={resolvedInputValue}
        disabled={disabled}
        loading={loading}
        options={options}
        includeInputInList
        getOptionLabel={getOptionLabel}
        getOptionKey={keyOf}
        isOptionEqualToValue={(option, value) => keyOf(option) === keyOf(value)}
        getOptionDisabled={getOptionDisabled}
        groupBy={groupBy}
        onChange={(_e, v) => {
          // inputValue is externally controlled: the parent commits a selection
          // through onInputChange and `value` follows it, so nothing is set here.
          onChange?.(v);
        }}
        onInputChange={(_e, v) => {
          onInputChange(v);
        }}
        renderOption={
          renderOption
            ? (props, option, state) => renderOption(props, option, state)
            : undefined
        }
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box
              sx={{
                m: 2,
                borderBottom: `1px solid ${theme.palette.primary.main}`,
                paddingBottom: theme.spacing(1),
              }}
            >
              {params.group}
            </Box>
            {params.children}
          </Box>
        )}
        renderInput={(params) => {
          return (
            <TextField
              {...rest}
              {...params}
              muiTextFieldLabel={label}
              errorMessage={errorMessage}
              showHelperText
              useFullWidth={true}
              name={name}
              InputProps={{
                ...params.InputProps,
                autoComplete: "off",
                type: "search",
                inputProps: {
                  maxLength,
                  ...params.inputProps,
                  ...inputProps,
                },
                startAdornment: SEARCH_ADORNMENT,
              }}
            />
          );
        }}
      />
    </Element>
  );
};
